import { jsPDF } from 'jspdf';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Generate Bill of Sale PDF
export const generateBillOfSalePDF = (data) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let y = 20;

  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL OF SALE', pageWidth / 2, y, { align: 'center' });
  
  y += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Document #: ${data.documentNumber}`, pageWidth / 2, y, { align: 'center' });
  
  y += 5;
  doc.text(`Date: ${formatDate(data.transaction?.saleDate || new Date())}`, pageWidth / 2, y, { align: 'center' });

  // Divider
  y += 10;
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);

  // Seller and Buyer Info - Two columns
  y += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('SELLER', 25, y);
  doc.text('BUYER', pageWidth / 2 + 10, y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  y += 8;
  doc.text(data.seller?.name || '', 25, y);
  doc.text(data.buyer?.name || '', pageWidth / 2 + 10, y);

  if (data.seller?.address?.street || data.buyer?.address?.street) {
    y += 5;
    doc.text(data.seller?.address?.street || '', 25, y);
    doc.text(data.buyer?.address?.street || '', pageWidth / 2 + 10, y);
  }

  if (data.seller?.address?.city || data.buyer?.address?.city) {
    y += 5;
    const sellerLocation = [
      data.seller?.address?.city,
      data.seller?.address?.state,
      data.seller?.address?.zipCode
    ].filter(Boolean).join(', ');
    const buyerLocation = [
      data.buyer?.address?.city,
      data.buyer?.address?.state,
      data.buyer?.address?.zipCode
    ].filter(Boolean).join(', ');
    doc.text(sellerLocation, 25, y);
    doc.text(buyerLocation, pageWidth / 2 + 10, y);
  }

  if (data.seller?.phone || data.buyer?.phone) {
    y += 5;
    doc.text(data.seller?.phone ? `Phone: ${data.seller.phone}` : '', 25, y);
    doc.text(data.buyer?.phone ? `Phone: ${data.buyer.phone}` : '', pageWidth / 2 + 10, y);
  }

  if (data.seller?.email || data.buyer?.email) {
    y += 5;
    doc.text(data.seller?.email || '', 25, y);
    doc.text(data.buyer?.email || '', pageWidth / 2 + 10, y);
  }

  // Divider
  y += 15;
  doc.line(20, y, pageWidth - 20, y);

  // Item Details
  y += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('ITEM DETAILS', 25, y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  y += 10;
  doc.text(`Description: ${data.item?.description || ''}`, 25, y);
  
  if (data.item?.make || data.item?.model) {
    y += 6;
    doc.text(`Make/Model: ${[data.item?.make, data.item?.model].filter(Boolean).join(' ')}`, 25, y);
  }
  
  if (data.item?.year) {
    y += 6;
    doc.text(`Year: ${data.item.year}`, 25, y);
  }
  
  if (data.item?.serialNumber) {
    y += 6;
    doc.text(`Serial Number: ${data.item.serialNumber}`, 25, y);
  }
  
  if (data.item?.condition) {
    y += 6;
    doc.text(`Condition: ${data.item.condition.charAt(0).toUpperCase() + data.item.condition.slice(1)}`, 25, y);
  }
  
  if (data.item?.color) {
    y += 6;
    doc.text(`Color: ${data.item.color}`, 25, y);
  }

  // Vehicle-specific fields
  if (data.templateType === 'vehicle' || data.item?.vin) {
    if (data.item?.vin) {
      y += 6;
      doc.text(`VIN: ${data.item.vin}`, 25, y);
    }
    if (data.item?.odometer) {
      y += 6;
      doc.text(`Odometer: ${data.item.odometer}`, 25, y);
    }
    if (data.item?.licensePlate) {
      y += 6;
      doc.text(`License Plate: ${data.item.licensePlate}`, 25, y);
    }
    if (data.item?.titleStatus && data.item.titleStatus !== '') {
      y += 6;
      const titleLabels = {
        'clean': 'Clean Title',
        'salvage': 'Salvage Title',
        'rebuilt': 'Rebuilt Title',
        'lien': 'Lien on Title',
        'bonded': 'Bonded Title'
      };
      doc.text(`Title Status: ${titleLabels[data.item.titleStatus] || data.item.titleStatus}`, 25, y);
    }
  }

  // Electronics-specific fields
  if (data.templateType === 'electronics' || data.item?.imei) {
    if (data.item?.imei) {
      y += 6;
      doc.text(`IMEI: ${data.item.imei}`, 25, y);
    }
    if (data.item?.storageCapacity) {
      y += 6;
      doc.text(`Storage: ${data.item.storageCapacity}`, 25, y);
    }
  }

  // Equipment-specific fields
  if (data.templateType === 'equipment' || data.item?.accessories) {
    if (data.item?.accessories) {
      y += 6;
      const accessories = doc.splitTextToSize(`Accessories Included: ${data.item.accessories}`, pageWidth - 50);
      doc.text(accessories, 25, y);
      y += (accessories.length - 1) * 5;
    }
  }

  if (data.item?.additionalDetails) {
    y += 6;
    const details = doc.splitTextToSize(`Additional Details: ${data.item.additionalDetails}`, pageWidth - 50);
    doc.text(details, 25, y);
    y += (details.length - 1) * 5;
  }

  // Transaction Details
  y += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TRANSACTION DETAILS', 25, y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text(`Sale Price: ${formatCurrency(data.transaction?.salePrice || 0)}`, 25, y);
  
  doc.setFont('helvetica', 'normal');
  y += 6;
  doc.text(`Payment Method: ${(data.transaction?.paymentMethod || 'cash').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}`, 25, y);
  
  if (data.transaction?.location) {
    y += 6;
    doc.text(`Sale Location: ${data.transaction.location}`, 25, y);
  }

  // Terms
  y += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS & CONDITIONS', 25, y);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  y += 8;
  
  if (data.terms?.asIs) {
    doc.text('• This item is sold "AS IS" with no warranties or guarantees unless otherwise specified.', 25, y);
    y += 5;
  }
  
  if (data.terms?.warranty && data.terms?.warrantyDetails) {
    doc.text(`• Warranty: ${data.terms.warrantyDetails}`, 25, y);
    y += 5;
  }
  
  doc.text('• The seller certifies they have the legal right to sell this item.', 25, y);
  y += 5;
  doc.text('• Both parties acknowledge this document as a record of the transaction.', 25, y);

  // Signatures
  y += 15;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('SIGNATURES', 25, y);
  
  y += 10;
  doc.setFont('helvetica', 'normal');
  
  // Check if we have actual signatures (e-signatures)
  if (data.sellerSignature) {
    try {
      doc.addImage(data.sellerSignature, 'PNG', 25, y, 60, 20);
    } catch (e) {
      doc.text('_________________________________', 25, y + 15);
    }
  } else {
    doc.text('_________________________________', 25, y + 15);
  }
  
  if (data.buyerSignature) {
    try {
      doc.addImage(data.buyerSignature, 'PNG', pageWidth / 2 + 10, y, 60, 20);
    } catch (e) {
      doc.text('_________________________________', pageWidth / 2 + 10, y + 15);
    }
  } else {
    doc.text('_________________________________', pageWidth / 2 + 10, y + 15);
  }
  
  y += 20;
  doc.setFontSize(9);
  doc.text('Seller Signature', 25, y);
  doc.text('Buyer Signature', pageWidth / 2 + 10, y);
  
  y += 5;
  doc.text(`${data.seller?.name || ''}`, 25, y);
  doc.text(`${data.buyer?.name || ''}`, pageWidth / 2 + 10, y);
  
  y += 8;
  doc.text(`Date: ${data.sellerSignedAt ? formatDate(new Date(data.sellerSignedAt)) : '________________'}`, 25, y);
  doc.text(`Date: ${data.buyerSignedAt ? formatDate(new Date(data.buyerSignedAt)) : '________________'}`, pageWidth / 2 + 10, y);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('Generated with PrivateSold - privatesold.com', pageWidth / 2, 285, { align: 'center' });

  return doc;
};

// Generate Invoice PDF
export const generateInvoicePDF = (data) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let y = 20;

  // Header
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', pageWidth - 25, y, { align: 'right' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.text(`Invoice #: ${data.invoiceNumber}`, pageWidth - 25, y, { align: 'right' });
  y += 5;
  doc.text(`Date: ${formatDate(data.issueDate || new Date())}`, pageWidth - 25, y, { align: 'right' });
  if (data.dueDate) {
    y += 5;
    doc.text(`Due Date: ${formatDate(data.dueDate)}`, pageWidth - 25, y, { align: 'right' });
  }

  // From (Seller)
  y = 20;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('FROM:', 25, y);
  
  doc.setFont('helvetica', 'normal');
  y += 6;
  doc.text(data.from?.name || '', 25, y);
  if (data.from?.businessName) {
    y += 5;
    doc.text(data.from.businessName, 25, y);
  }
  if (data.from?.address?.street) {
    y += 5;
    doc.text(data.from.address.street, 25, y);
  }
  if (data.from?.address?.city) {
    y += 5;
    doc.text(`${data.from.address.city}, ${data.from.address.state} ${data.from.address.zipCode}`, 25, y);
  }
  if (data.from?.email) {
    y += 5;
    doc.text(data.from.email, 25, y);
  }
  if (data.from?.phone) {
    y += 5;
    doc.text(data.from.phone, 25, y);
  }

  // To (Buyer)
  y += 15;
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO:', 25, y);
  
  doc.setFont('helvetica', 'normal');
  y += 6;
  doc.text(data.to?.name || '', 25, y);
  if (data.to?.businessName) {
    y += 5;
    doc.text(data.to.businessName, 25, y);
  }
  if (data.to?.address?.street) {
    y += 5;
    doc.text(data.to.address.street, 25, y);
  }
  if (data.to?.address?.city) {
    y += 5;
    doc.text(`${data.to.address.city}, ${data.to.address.state} ${data.to.address.zipCode}`, 25, y);
  }
  if (data.to?.email) {
    y += 5;
    doc.text(data.to.email, 25, y);
  }

  // Items Table
  y += 20;
  
  // Table Header
  doc.setFillColor(79, 70, 229); // Primary color
  doc.rect(20, y, pageWidth - 40, 10, 'F');
  
  doc.setTextColor(255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('DESCRIPTION', 25, y + 7);
  doc.text('QTY', pageWidth - 85, y + 7);
  doc.text('PRICE', pageWidth - 60, y + 7);
  doc.text('AMOUNT', pageWidth - 35, y + 7, { align: 'right' });
  
  doc.setTextColor(0);
  doc.setFont('helvetica', 'normal');
  
  y += 15;
  
  // Table Items
  (data.items || []).forEach((item, index) => {
    if (y > 240) {
      doc.addPage();
      y = 20;
    }
    
    // Alternate row colors
    if (index % 2 === 0) {
      doc.setFillColor(249, 250, 251);
      doc.rect(20, y - 5, pageWidth - 40, 10, 'F');
    }
    
    const description = doc.splitTextToSize(item.description, 80);
    doc.text(description, 25, y);
    doc.text(item.quantity.toString(), pageWidth - 85, y);
    doc.text(formatCurrency(item.unitPrice), pageWidth - 60, y);
    doc.text(formatCurrency(item.amount || item.quantity * item.unitPrice), pageWidth - 35, y, { align: 'right' });
    
    y += 10;
  });

  // Totals
  y += 10;
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 90, y, pageWidth - 20, y);
  
  y += 10;
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', pageWidth - 90, y);
  doc.text(formatCurrency(data.subtotal || 0), pageWidth - 35, y, { align: 'right' });

  if (data.taxRate > 0) {
    y += 8;
    doc.text(`Tax (${data.taxRate}%):`, pageWidth - 90, y);
    doc.text(formatCurrency(data.taxAmount || 0), pageWidth - 35, y, { align: 'right' });
  }

  if (data.discount > 0) {
    y += 8;
    const discountLabel = data.discountType === 'percentage' 
      ? `Discount (${data.discount}%):` 
      : 'Discount:';
    doc.text(discountLabel, pageWidth - 90, y);
    doc.text(`-${formatCurrency(data.discountType === 'percentage' ? data.subtotal * data.discount / 100 : data.discount)}`, pageWidth - 35, y, { align: 'right' });
  }

  y += 10;
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 90, y, pageWidth - 20, y);
  
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('TOTAL:', pageWidth - 90, y);
  doc.text(formatCurrency(data.total || 0), pageWidth - 35, y, { align: 'right' });

  // Payment Info
  if (data.paymentInstructions) {
    y += 25;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Instructions:', 25, y);
    doc.setFont('helvetica', 'normal');
    y += 6;
    const instructions = doc.splitTextToSize(data.paymentInstructions, pageWidth - 50);
    doc.text(instructions, 25, y);
  }

  // Notes
  if (data.notes) {
    y += 20;
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 25, y);
    doc.setFont('helvetica', 'normal');
    y += 6;
    const notes = doc.splitTextToSize(data.notes, pageWidth - 50);
    doc.text(notes, 25, y);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('Generated with PrivateSold - privatesold.com', pageWidth / 2, 285, { align: 'center' });

  return doc;
};

export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

// Generate Blank Bill of Sale PDF Template
export const generateBlankBillOfSalePDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let y = 20;

  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL OF SALE', pageWidth / 2, y, { align: 'center' });
  
  y += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Date: _______________________', pageWidth / 2, y, { align: 'center' });

  // Divider
  y += 10;
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);

  // Helper function to draw a labeled line
  const drawField = (label, x, yPos, lineWidth = 70) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(label, x, yPos);
    doc.line(x, yPos + 2, x + lineWidth, yPos + 2);
    return yPos + 12;
  };

  // Seller and Buyer Info - Two columns
  y += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('SELLER', 25, y);
  doc.text('BUYER', pageWidth / 2 + 10, y);

  y += 10;
  y = drawField('Name:', 25, y);
  drawField('Name:', pageWidth / 2 + 10, y - 12);
  
  y = drawField('Address:', 25, y);
  drawField('Address:', pageWidth / 2 + 10, y - 12);
  
  y = drawField('City, State, ZIP:', 25, y);
  drawField('City, State, ZIP:', pageWidth / 2 + 10, y - 12);
  
  y = drawField('Phone:', 25, y);
  drawField('Phone:', pageWidth / 2 + 10, y - 12);
  
  y = drawField('Email:', 25, y);
  drawField('Email:', pageWidth / 2 + 10, y - 12);

  // Divider
  y += 5;
  doc.line(20, y, pageWidth - 20, y);

  // Item Details
  y += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('ITEM DETAILS', 25, y);

  y += 10;
  y = drawField('Description:', 25, y, 150);
  y = drawField('Make/Model:', 25, y, 80);
  y = drawField('Year:', 25, y, 40);
  y = drawField('Serial/VIN Number:', 25, y, 80);
  y = drawField('Condition:', 25, y, 60);
  y = drawField('Color:', 25, y, 50);

  // Transaction Details
  y += 5;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TRANSACTION DETAILS', 25, y);

  y += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Sale Price: $', 25, y);
  doc.line(55, y + 2, 120, y + 2);
  
  y += 12;
  doc.text('Payment Method:  [ ] Cash   [ ] Check   [ ] Bank Transfer   [ ] Other: ____________', 25, y);
  
  y += 12;
  y = drawField('Sale Location:', 25, y, 100);

  // Terms
  y += 5;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TERMS & CONDITIONS', 25, y);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.text('[ ] This item is sold "AS IS" with no warranties or guarantees.', 25, y);
  y += 6;
  doc.text('[ ] Warranty provided: _________________________________________________', 25, y);
  y += 6;
  doc.text('• The seller certifies they have the legal right to sell this item.', 25, y);
  y += 5;
  doc.text('• Both parties acknowledge this document as a record of the transaction.', 25, y);

  // Signatures
  y += 15;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('SIGNATURES', 25, y);
  
  y += 10;
  doc.setFont('helvetica', 'normal');
  
  // Signature lines
  doc.text('_________________________________', 25, y + 15);
  doc.text('_________________________________', pageWidth / 2 + 10, y + 15);
  
  y += 20;
  doc.setFontSize(9);
  doc.text('Seller Signature', 25, y);
  doc.text('Buyer Signature', pageWidth / 2 + 10, y);
  
  y += 8;
  doc.text('Printed Name: ____________________', 25, y);
  doc.text('Printed Name: ____________________', pageWidth / 2 + 10, y);
  
  y += 8;
  doc.text('Date: ____________________________', 25, y);
  doc.text('Date: ____________________________', pageWidth / 2 + 10, y);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('Generated with PrivateSold - privatesold.com', pageWidth / 2, 285, { align: 'center' });

  return doc;
};

// Generate Blank Invoice PDF Template
export const generateBlankInvoicePDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let y = 20;

  // Header
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', pageWidth - 25, y, { align: 'right' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.text('Invoice #: ________________', pageWidth - 25, y, { align: 'right' });
  y += 6;
  doc.text('Date: ____________________', pageWidth - 25, y, { align: 'right' });
  y += 6;
  doc.text('Due Date: ________________', pageWidth - 25, y, { align: 'right' });

  // From (Seller)
  y = 20;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('FROM:', 25, y);
  
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.text('Name: _____________________________', 25, y);
  y += 6;
  doc.text('Business: __________________________', 25, y);
  y += 6;
  doc.text('Address: ___________________________', 25, y);
  y += 6;
  doc.text('City, State, ZIP: ____________________', 25, y);
  y += 6;
  doc.text('Email: _____________________________', 25, y);
  y += 6;
  doc.text('Phone: _____________________________', 25, y);

  // To (Buyer)
  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO:', 25, y);
  
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.text('Name: _____________________________', 25, y);
  y += 6;
  doc.text('Business: __________________________', 25, y);
  y += 6;
  doc.text('Address: ___________________________', 25, y);
  y += 6;
  doc.text('City, State, ZIP: ____________________', 25, y);
  y += 6;
  doc.text('Email: _____________________________', 25, y);

  // Items Table
  y += 15;
  
  // Table Header
  doc.setFillColor(79, 70, 229);
  doc.rect(20, y, pageWidth - 40, 10, 'F');
  
  doc.setTextColor(255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('DESCRIPTION', 25, y + 7);
  doc.text('QTY', pageWidth - 85, y + 7);
  doc.text('PRICE', pageWidth - 60, y + 7);
  doc.text('AMOUNT', pageWidth - 35, y + 7, { align: 'right' });
  
  doc.setTextColor(0);
  doc.setFont('helvetica', 'normal');
  
  y += 12;
  
  // Empty rows for items
  for (let i = 0; i < 5; i++) {
    y += 10;
    doc.line(25, y, pageWidth - 25, y);
  }

  // Totals section
  y += 20;
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', pageWidth - 90, y);
  doc.text('$ __________', pageWidth - 35, y, { align: 'right' });

  y += 10;
  doc.text('Tax (_____%): ', pageWidth - 90, y);
  doc.text('$ __________', pageWidth - 35, y, { align: 'right' });

  y += 10;
  doc.text('Discount:', pageWidth - 90, y);
  doc.text('$ __________', pageWidth - 35, y, { align: 'right' });

  y += 5;
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 90, y, pageWidth - 20, y);
  
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('TOTAL:', pageWidth - 90, y);
  doc.text('$ __________', pageWidth - 35, y, { align: 'right' });

  // Payment Info
  y += 20;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Method:', 25, y);
  doc.setFont('helvetica', 'normal');
  doc.text('[ ] Cash   [ ] Check   [ ] Bank Transfer   [ ] Other: ____________', 70, y);

  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Instructions:', 25, y);
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.line(25, y, pageWidth - 25, y);
  y += 8;
  doc.line(25, y, pageWidth - 25, y);

  // Notes
  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.text('Notes:', 25, y);
  doc.setFont('helvetica', 'normal');
  y += 8;
  doc.line(25, y, pageWidth - 25, y);
  y += 8;
  doc.line(25, y, pageWidth - 25, y);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('Generated with PrivateSold - privatesold.com', pageWidth / 2, 285, { align: 'center' });

  return doc;
};
